/**
 * Batch allocation for order processing (FEFO - first expiry, first out).
 *
 * An order line is filled from the batch closest to expiry, and spills into the
 * next-closest when that batch cannot cover it. The retailer can override any
 * of it afterwards - this only decides what is selected to begin with.
 *
 * Why the parsing is careful: retailer_stock_tbl.expiry_date is a STRING, and
 * uploads are normalised to MM/YYYY (see dateUtils.js). Sorting those as text
 * is wrong - "01/2027" sorts before "12/2026" but is nine months later - so
 * every comparison here runs on a parsed date. Older rows can still hold
 * YYYY-MM-DD, which normalizeExpiryDate handles.
 */
import moment from "moment";
import { normalizeExpiryDate } from "./dateUtils";

/** Stock quantities are stored as strings, so normalise before arithmetic. */
export const toQuantity = (value) => {
  const quantity = Number(
    String(value === null || value === undefined ? 0 : value).trim()
  );
  return Number.isFinite(quantity) ? quantity : 0;
};

//  Expiries that carry a real day. Only the unambiguous year-first shapes are
//  listed: DD/MM/YYYY and MM/DD/YYYY cannot be told apart from each other, so
//  those keep going through normalizeExpiryDate and its heuristics.
const DAY_FORMATS = ["YYYY-MM-DD", "YYYY/MM/DD"];

/** True when the stored value pins down a day, not just a month. */
export const hasDayPrecision = (value) =>
  value !== null &&
  value !== undefined &&
  moment(String(value).trim(), DAY_FORMATS, true).isValid();

/**
 * Parse a stored expiry into a moment, or null when it cannot be read.
 *
 * A value that carries a day keeps it. Only MM/YYYY values fall back to the
 * first of the month, because that is genuinely all they say.
 */
export const parseExpiry = (value) => {
  if (value === null || value === undefined) return null;

  const exact = moment(String(value).trim(), DAY_FORMATS, true);
  if (exact.isValid()) return exact;

  const normalised = normalizeExpiryDate(value);
  if (!normalised) return null;
  const parsed = moment(normalised, "MM/YYYY", true);
  return parsed.isValid() ? parsed : null;
};

/**
 * Has this batch passed its expiry?
 *
 * Compared at whatever precision the stored value actually has. Rounding a
 * dated row down to its month keeps selling it for the rest of that month -
 * a batch dated the 1st stayed selectable on the 9th - and disagrees with the
 * stock screen, which reads the same value at day precision.
 */
export const isExpiredBatch = (value, asOf) => {
  const parsed = parseExpiry(value);
  if (!parsed) return false;
  const now = asOf || moment();
  return parsed.isBefore(now, hasDayPrecision(value) ? "day" : "month");
};

/** A batch is usable if it has stock left and has not passed its expiry month. */
export const isUsableBatch = (stock) => {
  if (!stock) return false;
  if (toQuantity(stock.quantity) <= 0) return false;
  if (String(stock.status) === "0") return false;

  //  An unreadable expiry is never auto-selected - shipping stock whose date we
  //  cannot verify is not a decision to make automatically. It stays in the
  //  dropdown for the retailer to pick deliberately.
  if (!parseExpiry(stock.expiry_date)) return false;

  return !isExpiredBatch(stock.expiry_date);
};

/**
 * Order batches by which should be consumed first.
 *   1. nearest expiry
 *   2. smaller lot, so part-used batches are cleared rather than fragmented
 *   3. id, purely so the order is stable between renders
 */
export const compareByExpiry = (a, b) => {
  const expiryA = parseExpiry(a.expiry_date);
  const expiryB = parseExpiry(b.expiry_date);

  if (expiryA && expiryB) {
    const diff = expiryA.valueOf() - expiryB.valueOf();
    if (diff !== 0) return diff;
  } else if (expiryA) {
    return -1;
  } else if (expiryB) {
    return 1;
  }

  const quantityDiff = toQuantity(a.quantity) - toQuantity(b.quantity);
  if (quantityDiff !== 0) return quantityDiff;

  return Number(a.id || 0) - Number(b.id || 0);
};

/** Sort every batch for display, nearest expiry first. Does not mutate input. */
export const sortBatchesByExpiry = (stockRows) =>
  (stockRows || []).slice().sort(compareByExpiry);

/**
 * Build the default allocation for one order line.
 *
 * @param {Array}  stockRows   batches of this medicine held by the retailer
 * @param {number} requiredQty quantity ordered
 * @returns {{allocations: Array, shortfall: number}}
 *          allocations are in consumption order; shortfall is what no batch
 *          could cover (0 when the order is fully met).
 */
export const allocateFEFO = (stockRows, requiredQty) => {
  const required = Math.max(0, Math.trunc(toQuantity(requiredQty)));
  const candidates = (stockRows || []).filter(isUsableBatch).sort(compareByExpiry);

  let remaining = required;
  const allocations = [];

  for (let i = 0; i < candidates.length; i += 1) {
    if (remaining <= 0) break;

    const stock = candidates[i];
    const available = Math.trunc(toQuantity(stock.quantity));
    const take = Math.min(remaining, available);
    if (take <= 0) continue;

    allocations.push(buildAllocation(stock, take));
    remaining -= take;
  }

  return { allocations, shortfall: remaining };
};

/** SGST, falling back to CGST for stock rows that carry no SGST of their own. */
const sgstOf = (stock) =>
  stock.SGST !== null && stock.SGST !== undefined && stock.SGST !== ""
    ? stock.SGST
    : stock.CGST;

/**
 * The value Retailerdashboard puts on a batch <option>, rebuilt here so the
 * extra rows are read by the invoice collector exactly like the row above them.
 * That collector splits on "~" and takes fixed positions:
 *
 *   0 batch, 1 expiry, 2 quantity, 3 stock id, 4 mrp, 5 medicine id,
 *   6 discount, 7 CGST, 8 SGST, 9 IGST
 */
export const stockOptionValue = (stock) => {
  if (!stock) return "";
  return [
    stock.batch,
    stock.expiry_date,
    stock.quantity,
    stock.id,
    stock.mrp,
    stock.medicine_id,
    stock.discount,
    stock.CGST,
    sgstOf(stock),
    stock.IGST,
  ].join("~");
};

/** The stock row id embedded in one of those option values. */
export const stockIdFromOptionValue = (value) => {
  if (!value || value === "select batch") return null;
  const parts = String(value).split("~");
  return parts.length > 3 ? String(parts[3]) : null;
};

/**
 * One row in the processing table. `key` is a stable React key: rows are added
 * and removed while the retailer works, and reusing an index would let React
 * carry one row's input values onto another.
 */
export const buildAllocation = (stock, quantity) => ({
  key: "alloc-" + (stock && stock.id ? stock.id : Math.random().toString(36).slice(2)),
  stockId: stock ? stock.id : null,
  optionValue: stockOptionValue(stock),
  batch: stock ? stock.batch : "",
  expiry: stock ? stock.expiry_date : "",
  available: Math.trunc(toQuantity(stock ? stock.quantity : 0)),
  qty: Math.max(0, Math.trunc(toQuantity(quantity))),
  mrp: stock ? stock.mrp : "",
  discount: stock && stock.discount != null ? stock.discount : 0,
  CGST: stock && stock.CGST != null ? stock.CGST : 0,
  SGST: stock && stock.CGST != null ? sgstOf(stock) : 0,
  IGST: stock && stock.IGST != null ? stock.IGST : 0,
});

/**
 * Turn the server's suggested_batches (getOrderDetailsweb) into the same row
 * shape buildAllocation produces locally, so a server suggestion and a
 * hand-picked batch are indistinguishable to the rest of the UI.
 *
 * Matches each suggestion back to its full stock record by id where possible,
 * so the row carries every field (mrp, GST, etc.) even if the server only sent
 * the allocation, not the whole stock row. Falls back to the fields the
 * suggestion itself carries when no match is found - a batch the server saw
 * but that dropped out of stockRows between the two reads, for instance.
 *
 * Falls back to allocateFEFO entirely when the server did not send a
 * suggestion (older backend, or the field is empty), so this screen keeps
 * working against a backend that predates suggested_batches.
 */
export const resolveSuggestedAllocations = (line) => {
  const fromServer = line && line.suggestedBatches;

  if (Array.isArray(fromServer) && fromServer.length > 0) {
    return fromServer.map((entry) => {
      const stock = (line.stockRows || []).find(
        (row) => String(row.id) === String(entry.retailer_stock_id)
      );

      return stock
        ? buildAllocation(stock, entry.quantity)
        : buildAllocation(
            {
              id: entry.retailer_stock_id,
              batch: entry.batch,
              expiry_date: entry.expiry_date,
              quantity: entry.available,
              medicine_id: entry.medicine_id,
              mrp: entry.mrp,
              discount: entry.discount,
              CGST: entry.CGST,
              SGST: entry.SGST,
              IGST: entry.IGST,
            },
            entry.quantity
          );
    });
  }

  return allocateFEFO(line ? line.stockRows : [], line ? line.orderedQty : 0)
    .allocations;
};

/** An empty row, for "+ Add batch" before a batch has been chosen. */
export const emptyAllocation = () => ({
  key: "alloc-new-" + Math.random().toString(36).slice(2),
  stockId: null,
  optionValue: "",
  batch: "",
  expiry: "",
  available: 0,
  qty: 0,
  mrp: "",
  discount: 0,
  CGST: 0,
  SGST: 0,
  IGST: 0,
});

/** Total already allocated across the rows of one order line. */
export const allocatedTotal = (allocations) =>
  (allocations || []).reduce((sum, a) => sum + toQuantity(a.qty), 0);

/**
 * Why no batch could be selected automatically for a line.
 *
 * A line with only expired or empty batches leaves its dropdown on "select
 * batch", which reads as the screen having failed rather than as the retailer
 * being out of sellable stock. This says which it is, in the retailer's words,
 * so it can be shown on the line itself.
 *
 * @returns {string|null} null when at least one batch is sellable - there is
 *          nothing to explain - otherwise a short reason.
 */
export const unsellableReason = (stockRows) => {
  const rows = stockRows || [];
  if (rows.some(isUsableBatch)) return null;
  if (rows.length === 0) return "No batches in stock for this product";

  //  Named in the order the retailer can act on them: restocking answers the
  //  first two, and only a stock-screen correction answers the third.
  const expired = rows.filter((stock) => isExpiredBatch(stock.expiry_date));
  const empty = rows.filter((stock) => toQuantity(stock.quantity) <= 0);
  const unreadable = rows.filter((stock) => !parseExpiry(stock.expiry_date));

  if (expired.length === rows.length) return "All batches have expired";
  if (empty.length === rows.length) return "All batches are out of stock";
  if (unreadable.length === rows.length)
    return "Batch expiry dates are unreadable - fix them in Manage Stock";

  return "No in-stock, in-date batch available";
};

export default {
  allocateFEFO,
  isExpiredBatch,
  unsellableReason,
  hasDayPrecision,
  stockOptionValue,
  stockIdFromOptionValue,
  sortBatchesByExpiry,
  compareByExpiry,
  isUsableBatch,
  buildAllocation,
  emptyAllocation,
  allocatedTotal,
  parseExpiry,
  toQuantity,
};
